/**
 * PIEStreakPill — tiny "weekly discipline" badge that pulls users
 * back to log their pulse on time. Probes /pulse/history to get the
 * persona-wide streak data (window: 4 weeks) and renders:
 *
 *   • 4/4 weeks → emerald 🔥 "On a roll: 4 of 4"
 *   • 2-3/4    → amber  "Keeping up: N of 4"
 *   • 0-1/4    → muted  "Get a streak going"
 *
 * Click → jumps to the Pulse history page so the user can see their
 * full record.
 *
 * Research backing: streaks are the strongest weekly-habit anchor —
 * better than reminder emails (Clear, Atomic Habits ch.13; multiple
 * Quantified Self meta-analyses).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame } from 'lucide-react';
import { pieApi } from './pieApi';
import logger from '../../lib/logger';

export default function PIEStreakPill() {
  const [streak, setStreak] = useState(null);

  useEffect(() => {
    pieApi.pulseCurrent()
      .then((d) => { if (d?.streak) setStreak(d.streak); })
      .catch((e) => logger.warn('streak probe failed', e));
  }, []);

  if (!streak) return null;

  const { logged_weeks, total_weeks, weeks } = streak;
  const pct = logged_weeks / total_weeks;

  const tone = pct >= 1
    ? { color: '#065F46', bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.35)', label: 'On a roll' }
    : pct >= 0.5
    ? { color: '#92400E', bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.35)', label: 'Keeping up' }
    : { color: 'var(--pie-text-secondary)', bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.25)', label: 'Get a streak going' };

  return (
    <Link
      to="/mirror/pulses"
      data-testid="pie-streak-pill"
      title="See your full pulse history"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 10,
        padding: '6px 14px', borderRadius: 999,
        background: tone.bg, border: `1px solid ${tone.border}`,
        color: tone.color, fontSize: 12, fontWeight: 600,
        textDecoration: 'none',
        transition: 'box-shadow 0.15s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(15, 23, 42, 0.08)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <Flame size={13} aria-hidden style={{ flexShrink: 0 }} />
      <span>{tone.label}</span>
      <span style={{ opacity: 0.65, fontWeight: 500 }}>·</span>
      <span data-testid="pie-streak-count">
        {logged_weeks} of last {total_weeks} weeks
      </span>
      {weeks && (
        <span
          aria-hidden
          style={{ display: 'inline-flex', gap: 3, marginLeft: 2 }}
        >
          {weeks.map((w) => (
            <span
              key={w.week_iso}
              style={{
                width: 6, height: 6, borderRadius: '50%',
                background: w.logged ? tone.color : 'transparent',
                border: `1.5px solid ${tone.color}`,
                opacity: w.logged ? 0.9 : 0.35,
              }}
            />
          ))}
        </span>
      )}
    </Link>
  );
}
